import React, { useState } from 'react';

interface PaymentScreenProps {
  order: any;
  onPaymentComplete: () => void;
  onClose?: () => void;
}

const PaymentScreen: React.FC<PaymentScreenProps> = ({ order, onPaymentComplete, onClose }) => {
  const [paymentMethod, setPaymentMethod] = useState<'upi' | 'phonepe' | 'gpay' | 'paytm'>('phonepe');
  const [upiId, setUpiId] = useState('');
  const [validating, setValidating] = useState(false);
  const [processing, setProcessing] = useState(false);

  const merchantUPI = '9652333814@paytm'; // Your business UPI ID
  const totalAmount = order.totalAmount || 0;
  const orderId = order._id?.slice(-6) || 'ORDER';

  // Validate UPI ID format
  const validateUPI = (id: string): boolean => {
    const upiRegex = /^[\w.-]+@[\w.-]+$/;
    return upiRegex.test(id);
  };

  const handleUPIValidation = () => {
    if (!upiId.trim()) {
      alert('Please enter your UPI ID');
      return;
    }

    if (!validateUPI(upiId)) {
      alert('Invalid UPI ID format. Example: yourname@paytm or 9876543210@ybl');
      return;
    }

    setValidating(true);
    setTimeout(() => {
      setValidating(false);
      alert(`✅ UPI ID validated: ${upiId}\n\nProceeding to payment...`);
      handleDirectPayment();
    }, 1000);
  };

  const handleDirectPayment = () => {
    setProcessing(true);

    const baseParams = `pa=${merchantUPI}&pn=ThunderBag&am=${totalAmount}&cu=INR&tn=Order${orderId}`;
    
    let paymentUrl = '';

    switch (paymentMethod) {
      case 'phonepe':
        // PhonePe deep link
        paymentUrl = `phonepe://pay?${baseParams}`;
        // Fallback to web
        setTimeout(() => {
          window.location.href = `https://phon.pe/ru_${btoa(baseParams)}`;
        }, 500);
        break;
      
      case 'gpay':
        // Google Pay deep link
        paymentUrl = `tez://upi/pay?${baseParams}`;
        // Fallback to intent
        setTimeout(() => {
          window.location.href = `upi://pay?${baseParams}`;
        }, 500);
        break;
      
      case 'paytm':
        // Paytm deep link
        paymentUrl = `paytmmp://pay?${baseParams}`;
        // Fallback to intent
        setTimeout(() => {
          window.location.href = `upi://pay?${baseParams}`;
        }, 500);
        break;
      
      case 'upi':
      default:
        // Generic UPI intent (opens default UPI app)
        paymentUrl = `upi://pay?${baseParams}`;
        break;
    }

    // Open payment app
    window.location.href = paymentUrl;

    // Show payment confirmation dialog after delay
    setTimeout(() => {
      setProcessing(false);
      const confirmed = window.confirm(
        '💳 Payment Status\n\n' +
        'Have you completed the payment?\n\n' +
        '✅ Click OK if payment is successful\n' +
        '❌ Click Cancel to try again'
      );

      if (confirmed) {
        onPaymentComplete();
      }
    }, 3000);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>💳 Complete Payment</h2>
          <p style={styles.subtitle}>Partner is waiting at store with your items</p>
        </div>

        {/* Bill Image */}
        {order.billImage && (
          <div style={styles.billContainer}>
            <img src={order.billImage} alt="Bill" style={styles.billImage} />
          </div>
        )}

        {/* Amount Breakdown */}
        <div style={styles.amountBox}>
          <div style={styles.row}>
            <span>Items Total:</span>
            <span>₹{order.itemsTotal?.toFixed(2) || '0.00'}</span>
          </div>
          <div style={styles.row}>
            <span>Delivery Charges:</span>
            <span>₹{order.deliveryCharges?.toFixed(2) || '50.00'}</span>
          </div>
          <div style={styles.divider}></div>
          <div style={styles.rowTotal}>
            <span style={styles.totalLabel}>Total Amount:</span>
            <span style={styles.totalAmount}>₹{totalAmount.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment Method Selection */}
        <div style={styles.methodSection}>
          <label style={styles.label}>Select Payment App:</label>
          <div style={styles.methodGrid}>
            <button
              style={{
                ...styles.methodBtn,
                ...(paymentMethod === 'phonepe' ? styles.methodBtnActive : {})
              }}
              onClick={() => setPaymentMethod('phonepe')}
            >
              <span style={styles.methodIcon}>📱</span>
              <span>PhonePe</span>
            </button>
            <button
              style={{
                ...styles.methodBtn,
                ...(paymentMethod === 'gpay' ? styles.methodBtnActive : {})
              }}
              onClick={() => setPaymentMethod('gpay')}
            >
              <span style={styles.methodIcon}>🅶</span>
              <span>Google Pay</span>
            </button>
            <button
              style={{
                ...styles.methodBtn,
                ...(paymentMethod === 'paytm' ? styles.methodBtnActive : {})
              }}
              onClick={() => setPaymentMethod('paytm')}
            >
              <span style={styles.methodIcon}>💙</span>
              <span>Paytm</span>
            </button>
            <button
              style={{
                ...styles.methodBtn,
                ...(paymentMethod === 'upi' ? styles.methodBtnActive : {})
              }}
              onClick={() => setPaymentMethod('upi')}
            >
              <span style={styles.methodIcon}>💳</span>
              <span>Other UPI</span>
            </button>
          </div>
        </div>

        {/* UPI ID Input */}
        <div style={styles.upiSection}>
          <label style={styles.label}>Your UPI ID (Optional - for verification):</label>
          <input
            type="text"
            placeholder="yourname@paytm or 9876543210@ybl"
            value={upiId}
            onChange={(e) => setUpiId(e.target.value)}
            style={styles.input}
          />
          <p style={styles.hint}>💡 Enter your UPI ID for faster verification</p>
        </div>

        {/* Payment Button */}
        <button
          style={{
            ...styles.payBtn,
            opacity: processing || validating ? 0.7 : 1
          }}
          onClick={upiId ? handleUPIValidation : handleDirectPayment}
          disabled={processing || validating}
        >
          {processing ? (
            <>⏳ Opening Payment App...</>
          ) : validating ? (
            <>🔄 Validating UPI...</>
          ) : (
            <>💰 Pay ₹{totalAmount.toFixed(2)} Now</>
          )}
        </button>

        {/* Info Message */}
        <div style={styles.infoBox}>
          <p style={styles.infoText}>
            🔒 <strong>Secure Payment</strong>
          </p>
          <p style={styles.infoDesc}>
            You'll be redirected to your payment app. Complete the payment and return to this app.
          </p>
        </div>

        {/* Cancel option removed - payment is mandatory */}
      </div>
    </div>
  );
};

const styles: any = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    padding: '20px',
    overflowY: 'auto',
  },
  container: {
    background: 'white',
    borderRadius: '20px',
    padding: '30px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#6366f1',
    fontWeight: '600',
  },
  billContainer: {
    marginBottom: '20px',
    textAlign: 'center',
  },
  billImage: {
    maxWidth: '100%',
    maxHeight: '200px',
    borderRadius: '12px',
    border: '2px solid #e0e0e0',
  },
  amountBox: {
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
    fontSize: '15px',
    color: '#34495e',
  },
  divider: {
    height: '2px',
    background: '#bdc3c7',
    margin: '10px 0',
  },
  rowTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalLabel: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  totalAmount: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#6366f1',
  },
  methodSection: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#34495e',
    marginBottom: '10px',
  },
  methodGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
  },
  methodBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '15px',
    background: 'white',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '14px',
    fontWeight: '600',
  },
  methodBtnActive: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: '2px solid #667eea',
  },
  methodIcon: {
    fontSize: '32px',
  },
  upiSection: {
    marginBottom: '20px',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border 0.3s ease',
  },
  hint: {
    fontSize: '12px',
    color: '#7f8c8d',
    marginTop: '5px',
  },
  payBtn: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #6366f1 0%, #a78bfa 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: '15px',
    boxShadow: '0 4px 15px rgba(255, 111, 97, 0.4)',
  },
  infoBox: {
    background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
    borderRadius: '12px',
    padding: '15px',
    textAlign: 'center',
  },
  infoText: {
    fontSize: '15px',
    color: '#2e7d32',
    marginBottom: '5px',
  },
  infoDesc: {
    fontSize: '13px',
    color: '#558b2f',
    lineHeight: '1.5',
  },
};

export default PaymentScreen;
